import { request } from '@/api/apiUtils';
import { buildUrl } from '@/api/url';
import { createEntityQuery } from '@/data/createEntityQuery';
import type { EntityDefinition, FetcherEntry } from '@/data/types';
import { tagCategoryId } from '@/enums';
import type { ILine, ILinePresentations } from '@/types';

/** Root: query-key prefix for this entity's slice of the cache (ADR 0010) */
export const LINES_ROOT = ['lines'] as const;
/** Root: query-key prefix for this entity's slice of the cache (ADR 0010) */
export const LINE_PRESENTATIONS_ROOT = ['linePresentations'] as const;

interface LinesListParams {
    projectId: number;
    lineId?: number;
    version: string;
    query?: string;
}

interface LinesPresentationsParams {
    projectId: number;
    version: string;
    tags?: string;
}

interface LinesPresentationDetailParams {
    projectId: number;
    version: string;
    lineId: number;
    tags?: string;
}

interface LinesPresentationsListParams {
    projectId: number;
    version: string;
    tags?: string;
}

function normalizeTagToLine(tag: Record<string, unknown> & ILine): ILine {
    const name = (tag.name as string) || '';
    const title = tag.title || name;
    const code = tag.code || name;
    const entity_tag = tag.entity_tag || { id: tag.id, name, tag_category_id: tagCategoryId.lineTag, color: tag.color };
    return { ...tag, title, code, entity_tag } as ILine;
}

const fetchers = {
    list: {
        key: (args: LinesListParams) => [...LINES_ROOT, 'list', args] as const,
        fetch: async ({ projectId, lineId, version }: LinesListParams): Promise<ILine[] | ILine> => {
            const lineIdPath = lineId ? `/${lineId}` : '';
            const url = buildUrl(`projects/${projectId}/lines${lineIdPath}`, { version });
            const response: { data: ILine[] | ILine } = await request(url);
            const data = response.data;
            return Array.isArray(data)
                ? data.map(item => normalizeTagToLine(item as Record<string, unknown> & ILine))
                : normalizeTagToLine(data as Record<string, unknown> & ILine);
        },
    },
    presentations: {
        key: (args: LinesPresentationsParams) => [...LINE_PRESENTATIONS_ROOT, 'list', args] as const,
        fetch: async ({ projectId, version, tags }: LinesPresentationsParams): Promise<ILinePresentations[]> => {
            const url = buildUrl(
                `projects/${projectId}/presentations/tags-search`,
                { tag_category_id: tagCategoryId.lineTag, version },
                tags,
            );
            const response: { data: ILinePresentations[] } = await request(url);
            return response.data;
        },
    },
    presentationDetail: {
        key: (args: LinesPresentationDetailParams) => [...LINE_PRESENTATIONS_ROOT, 'detail', args] as const,
        fetch: async ({ projectId, version, lineId, tags }: LinesPresentationDetailParams): Promise<ILinePresentations> => {
            const url = buildUrl(
                `projects/${projectId}/presentations/tags/${lineId}`,
                { tag_category_id: tagCategoryId.lineTag, version },
                tags,
            );
            const response: { data: ILinePresentations } = await request(url);
            return response.data;
        },
    },
    presentationsList: {
        key: (args: LinesPresentationsListParams) => [...LINE_PRESENTATIONS_ROOT, 'list', { ...args, view: 'old' }] as const,
        fetch: async ({ projectId, version, tags }: LinesPresentationsListParams): Promise<ILinePresentations[]> => {
            const views = 'view[]=sequences&view[]=scenes&view[]=lines';
            const url = buildUrl(
                `projects/${projectId}/presentations/tags`,
                { tag_category_id: tagCategoryId.lineTag, version },
                views,
                tags,
            );
            const response: { data: ILinePresentations[] } = await request(url);
            return response.data;
        },
    },
    // biome-ignore lint/suspicious/noExplicitAny: required for generic type inference
} satisfies Record<string, FetcherEntry<any, any>>;

export const linesEntity: EntityDefinition<typeof fetchers> = {
    name: 'lines',
    roots: [LINES_ROOT, LINE_PRESENTATIONS_ROOT],
    fetchers,
};

export const useLinesList = createEntityQuery(linesEntity, 'list');
export const useLinesPresentations = createEntityQuery(linesEntity, 'presentations');
export const useLinesPresentationDetail = createEntityQuery(linesEntity, 'presentationDetail');
export const useLinesPresentationsList = createEntityQuery(linesEntity, 'presentationsList');

export type { LinesListParams, LinesPresentationDetailParams, LinesPresentationsListParams, LinesPresentationsParams };
